/**
 * Profile Hook
 *
 * Provides profile settings management, user tasks, and workload statistics.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useDatabase } from '@/context/DatabaseContext';
import { useOrganization } from '@/context/OrganizationContext';
import {
  getProfileSettings,
  upsertProfileSettings,
  generateShareToken as dbGenerateShareToken,
  type ProfileSettingsUI,
} from '@/db/profiles';
import { isCompletedStatus, normalizeStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { handleError } from '@/utils/errorHandler';

export interface WorkloadStats {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  openTasks: number;
  blockedTasks: number;
  totalTimeEstimated: number;
  completionRate: number;
}

const DEFAULT_SETTINGS: Omit<ProfileSettingsUI, 'id' | 'userId' | 'orgId' | 'createdAt'> = {
  showCalendarToOrg: true,
  showWorkloadToOrg: true,
  showTasksToOrg: true,
  publicShareEnabled: false,
  publicShareToken: null,
  publicShowCalendar: true,
  defaultCalendarView: 'month',
  workingHoursStart: '09:00',
  workingHoursEnd: '18:00',
};

export function useProfile(targetUserId?: string) {
  const { user } = useAuth();
  const { tasks } = useDatabase();
  const { currentOrg } = useOrganization();

  const [profileSettings, setProfileSettings] = useState<ProfileSettingsUI | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const userId = targetUserId || user?.id || '';
  const orgId = currentOrg?.id || '';
  const isOwnProfile = !targetUserId || targetUserId === 'me' || targetUserId === user?.id;

  // Load settings
  useEffect(() => {
    if (!userId || !orgId || userId === 'me') {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const resolvedUserId = isOwnProfile ? (user?.id || '') : userId;
        if (!resolvedUserId) return;

        const settings = await getProfileSettings(resolvedUserId, orgId);
        if (!cancelled) {
          setProfileSettings(settings);
        }
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel caricamento impostazioni profilo' });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, orgId, isOwnProfile, user?.id]);

  // Update settings
  const updateProfileSettings = useCallback(
    async (updates: Partial<ProfileSettingsUI>) => {
      if (!user?.id || !orgId) return;

      try {
        const result = await upsertProfileSettings({
          userId: user.id,
          orgId,
          ...updates,
        });
        if (result) {
          setProfileSettings(result);
        }
      } catch (err) {
        handleError(err, { level: 'error', message: 'Errore nel salvataggio impostazioni profilo' });
      }
    },
    [user?.id, orgId]
  );

  // Generate share link
  const generateShareLink = useCallback(async (): Promise<string | null> => {
    if (!user?.id || !orgId) return null;

    try {
      const token = await dbGenerateShareToken(user.id, orgId);
      if (token) {
        // Reload settings to get the updated token
        const settings = await getProfileSettings(user.id, orgId);
        if (settings) {
          setProfileSettings(settings);
        }
        return `${window.location.origin}/p/${token}`;
      }
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nella generazione del link di condivisione' });
    }
    return null;
  }, [user?.id, orgId]);

  // Filter tasks for target user
  const resolvedUserId = isOwnProfile ? (user?.id || '') : userId;
  const userTasks = useMemo(() => {
    if (!resolvedUserId) return [];
    return tasks.filter(
      (t) => t.assignees && t.assignees.includes(resolvedUserId)
    );
  }, [tasks, resolvedUserId]);

  // Compute workload stats
  const workloadStats = useMemo((): WorkloadStats => {
    const now = Date.now();

    const totalTasks = userTasks.length;
    const completedTasks = userTasks.filter((t) => isCompletedStatus(t.status)).length;
    const inProgressTasks = userTasks.filter(
      (t) => normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS
    ).length;
    const blockedTasks = userTasks.filter(
      (t) => normalizeStatus(t.status) === TASK_STATUS.BLOCKED
    ).length;
    const openTasks = userTasks.filter((t) => {
      const s = normalizeStatus(t.status);
      return s === TASK_STATUS.OPEN || s === TASK_STATUS.TODO;
    }).length;
    const overdueTasks = userTasks.filter((t) => {
      if (!t.dueDate) return false;
      return t.dueDate < now && !isCompletedStatus(t.status);
    }).length;
    const totalTimeEstimated = userTasks.reduce((sum, t) => sum + (t.timeEstimated || 0), 0);
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      overdueTasks,
      openTasks,
      blockedTasks,
      totalTimeEstimated,
      completionRate,
    };
  }, [userTasks]);

  // Merge with defaults for display
  const effectiveSettings = useMemo(() => {
    if (profileSettings) return profileSettings;
    return {
      ...DEFAULT_SETTINGS,
      id: '',
      userId: resolvedUserId,
      orgId,
      createdAt: '',
    } as ProfileSettingsUI;
  }, [profileSettings, resolvedUserId, orgId]);

  return {
    profileSettings: effectiveSettings,
    isLoading,
    isOwnProfile,
    updateProfileSettings,
    generateShareLink,
    userTasks,
    workloadStats,
  };
}
